/**
 * Priority Badge Component
 *
 * Displays a priority with dynamic colors from the database.
 * Falls back to a neutral style if priority not found.
 */

import { cn } from '@/lib/utils'
import { usePriorities } from '@/application/priorities/usePriorities'
import type { Priority } from '@/domain/priority/types'

interface PriorityBadgeProps {
  /** Priority ID to display */
  priorityId?: string | null | undefined
  /** Priority number (1-4) - will be mapped to sortOrder */
  priorityNumber?: number | null | undefined
  /** Optional: pass priority object directly to avoid extra lookup */
  priority?: Priority | null
  /** Additional CSS classes */
  className?: string
  /** Size variant */
  size?: 'sm' | 'md' | 'lg'
}

/**
 * Badge that displays priority with configured colors
 */
export function PriorityBadge({
  priorityId,
  priorityNumber,
  priority: priorityProp,
  className,
  size = 'md',
}: PriorityBadgeProps) {
  const { data: priorities } = usePriorities()

  // Use passed priority, look up by ID, or look up by sortOrder (for legacy numeric priorities)
  const priority = priorityProp
    || (priorityId ? priorities?.find(p => p.id === priorityId) : null)
    || (priorityNumber ? priorities?.find(p => p.sortOrder === priorityNumber) : null)

  // Size classes
  const sizeClasses = {
    sm: 'px-1.5 py-0.5 text-[10px]',
    md: 'px-2 py-1 text-xs',
    lg: 'px-3 py-1.5 text-sm',
  }

  // If no priority found, show placeholder
  if (!priority) {
    return (
      <span
        className={cn(
          'inline-flex items-center font-medium rounded-full',
          'bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
          sizeClasses[size],
          className
        )}
      >
        Sin prioridad
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center font-medium rounded-full',
        sizeClasses[size],
        className
      )}
      style={{
        backgroundColor: priority.color,
        color: priority.textColor,
      }}
    >
      {priority.name}
    </span>
  )
}

/**
 * Compact badge showing just the priority code
 */
export function PriorityBadgeCompact({
  priorityId,
  priority: priorityProp,
  className,
}: Omit<PriorityBadgeProps, 'size'>) {
  const { data: priorities } = usePriorities()
  const priority = priorityProp || priorities?.find(p => p.id === priorityId)

  if (!priority) {
    return (
      <span
        className={cn(
          'inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded',
          'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
          className
        )}
      >
        -
      </span>
    )
  }

  return (
    <span
      className={cn(
        'inline-flex items-center justify-center w-6 h-6 text-[10px] font-bold rounded',
        className
      )}
      style={{
        backgroundColor: priority.color,
        color: priority.textColor,
      }}
      title={priority.name}
    >
      {priority.code.slice(0, 3)}
    </span>
  )
}

export default PriorityBadge
